/**
 * Phase 164 — Public Scout Profile (token-gated, no auth)
 *
 * Mounts at /scout/:slug/:jersey?exp=YYYYMMDD&token=...
 * Renders a read-only Phase 162-style longitudinal radar for a single
 * jersey. Embeds a "Powered by Out-Play" viral CTA in the footer with
 * src=viral_scout so Phase 151 telemetry captures click-through.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  Trophy, Hash, MapPin, ShieldCheck, Sparkles, ArrowRight, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = [
  { key: 'xg',            label: 'xG',            color: '#a855f7' },
  { key: 'pass_accuracy', label: 'Pass Acc %',    color: '#3b82f6' },
  { key: 'tackles_won',   label: 'Tackles Won %', color: '#ef4444' },
  { key: 'possession',    label: 'Possession %',  color: '#10b981' },
];

export default function PublicScoutProfile() {
  const { slug, jersey } = useParams();
  const [params] = useSearchParams();
  const exp = params.get('exp') || '';
  const token = params.get('token') || '';

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeMetrics, setActiveMetrics] = useState({
    xg: true, pass_accuracy: true, tackles_won: false, possession: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/public/scout/${slug}/${encodeURIComponent(jersey)}?exp=${exp}&token=${token}`
        );
        if (cancelled) return;
        setData(r.data);
        // Fire view telemetry (silent, fire-and-forget)
        axios.post(`${API}/public/scout/view`, {
          academy_slug: slug,
          jersey_number: r.data?.jersey_number || jersey,
          exp, token,
          referrer: document.referrer || '',
        }).catch(() => {});
      } catch (e) {
        setError(e?.response?.data?.detail || 'Could not load this profile.');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, jersey, exp, token]);

  const chartRows = useMemo(() => {
    if (!data?.timeline) return [];
    return data.timeline.map((p, i) => ({
      idx: i + 1,
      label: p.date,
      match_title: p.match_title,
      xg: p.kpis.xg,
      pass_accuracy: p.kpis.pass_accuracy,
      tackles_won: p.kpis.tackles_won,
      possession: p.kpis.possession,
    }));
  }, [data]);

  const toggleMetric = (k) =>
    setActiveMetrics((p) => ({ ...p, [k]: !p[k] }));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-500" data-testid="phase164-scout-loading">Loading scout profile…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="phase164-scout-error">
        <Card className="w-full max-w-md">
          <CardContent className="p-7 text-center space-y-3">
            <AlertCircle className="h-10 w-10 text-rose-500 mx-auto" />
            <h1 className="text-lg font-bold text-slate-800">Profile unavailable</h1>
            <p className="text-sm text-slate-600">{error || 'This share link may be expired or revoked.'}</p>
            <Link to="/">
              <Button variant="outline" className="w-full">Visit Out-Play</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { academy, jersey_number, match_count, averages } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/20" data-testid="phase164-scout-profile">
      {/* Top branding bar */}
      <div className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="phase164-scout-brand-link">
            <span className="font-display font-extrabold text-xl tracking-tight">
              <span className="text-slate-800">Out</span><span className="text-red-500">-Play</span>
            </span>
            <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[9px] font-bold uppercase tracking-wider">
              <Sparkles className="h-2.5 w-2.5 mr-1" /> Scout Profile
            </Badge>
          </Link>
          <Link to="/?src=viral_scout_navbar" data-testid="phase164-scout-cta-navbar">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg">
              Launch Your Academy →
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <Card className="border-purple-200/60 bg-gradient-to-br from-purple-50/40 via-white to-violet-50/30">
          <CardContent className="p-5 sm:p-7">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] font-bold uppercase tracking-wider">
                    <ShieldCheck className="h-2.5 w-2.5 mr-1" /> Verified Scout Profile
                  </Badge>
                  <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px] font-semibold">
                    {match_count} match{match_count === 1 ? '' : 'es'} on file
                  </Badge>
                </div>
                <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight"
                  data-testid="phase164-scout-jersey-heading">
                  <Hash className="inline h-6 w-6 text-purple-600 -translate-y-0.5" />
                  {' '}{jersey_number}
                </h1>
                <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
                  <Trophy className="h-4 w-4 text-amber-500" />
                  <span className="font-semibold" data-testid="phase164-scout-academy-name">{academy?.name}</span>
                  {academy?.city && (
                    <span className="inline-flex items-center gap-1 text-slate-500">
                      <MapPin className="h-3.5 w-3.5" /> {academy.city}
                    </span>
                  )}
                </div>
              </div>
              {averages && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:max-w-sm" data-testid="phase164-scout-averages-strip">
                  {METRICS.map((m) => (
                    <div key={m.key} className="bg-white border border-slate-200 rounded-xl px-3 py-2"
                      data-testid={`phase164-scout-avg-${m.key}`}>
                      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                        <span style={{ color: m.color }}>●</span> Avg {m.label}
                      </div>
                      <div className="text-base font-bold text-slate-800 leading-tight">
                        {averages[m.key] ?? '—'}{m.key !== 'xg' && '%'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Chart */}
        <Card>
          <CardContent className="p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-display font-semibold text-base text-slate-800">Longitudinal Progression</h2>
              <div className="flex flex-wrap gap-1.5" data-testid="phase164-scout-metric-toggles">
                {METRICS.map((m) => {
                  const on = activeMetrics[m.key];
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => toggleMetric(m.key)}
                      data-testid={`phase164-scout-metric-${m.key}`}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all ${
                        on ? 'text-white shadow-sm' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                      }`}
                      style={on ? { background: m.color, borderColor: m.color } : {}}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="min-h-[300px] bg-slate-50/40 border border-slate-100 rounded-xl p-2" data-testid="phase164-scout-chart-canvas">
              {match_count === 0 ? (
                <div className="h-72 flex items-center justify-center text-sm text-slate-500">No matches on file yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartRows} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <Tooltip
                      contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }}
                      labelFormatter={(label, payload) => {
                        const row = payload?.[0]?.payload;
                        return row ? `${row.match_title} · ${row.label}` : label;
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {METRICS.filter((m) => activeMetrics[m.key]).map((m) => (
                      <Line
                        key={m.key}
                        type="monotone"
                        dataKey={m.key}
                        name={m.label}
                        stroke={m.color}
                        strokeWidth={2.5}
                        dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                        activeDot={{ r: 6 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent matches list */}
        <Card>
          <CardContent className="p-4 sm:p-5">
            <h2 className="font-display font-semibold text-base text-slate-800 mb-3">Recent Matches</h2>
            <div className="space-y-2" data-testid="phase164-scout-recent-matches">
              {data.timeline.slice(-6).reverse().map((p, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 p-3 bg-slate-50/50 border border-slate-100 rounded-lg">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{p.match_title}</div>
                    <div className="text-[11px] text-slate-500">{p.date} · {p.sport}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 justify-end text-[11px]">
                    <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full font-semibold">xG {p.kpis.xg}</span>
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-semibold">Pass {p.kpis.pass_accuracy}%</span>
                    <span className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded-full font-semibold">Tk {p.kpis.tackles_won}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Viral conversion banner — sticky bottom */}
      <div className="sticky bottom-0 z-40 border-t border-slate-200 bg-gradient-to-r from-emerald-600 to-teal-600 text-white"
        data-testid="phase164-scout-viral-banner">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold min-w-0">
            <Sparkles className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">
              📊 Processed by the Out-Play AI Video Intelligence Engine —
              <span className="hidden sm:inline"> track your athletes like the pros.</span>
            </span>
          </div>
          <Link to="/?src=viral_scout" data-testid="phase164-scout-viral-cta">
            <Button size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold text-xs rounded-lg inline-flex items-center gap-1">
              Launch Your Academy <ArrowRight className="h-3 w-3" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
